import uiIcon from '@shared/ui-icon';
import templater from 'lodash-es/template';
import template from './template.html';
import 'css/flux/modules/Modal.less';

const OPEN_EVENT = {
  METHOD: 'method',
};

const CLOSE_EVENT = {
  ESC: 'esc',
  OUTOFBOUNDS: 'outofbounds',
  ELEMENT: 'element',
};

const TYPE = {
  CONFIRMATION: 'Modal--confirmation',
};

const SIZE = {
  SMALL: 'Modal--small',
  LARGE: 'Modal--large',
  FULLSCREEN: 'Modal--fullscreen',
};

export { SIZE, TYPE };

const defaultOptions = {
  main: document.querySelector('#main'),
  closeButton: true,
  backdropClose: true,
  extraClass: '',
  size: '',
  type: '',
};

// Template the modal's scaffolding by injecting header, body and footer
const scaffold = templater(template);

/**
 * Modal constructor
 * @param {Node} node Dialog element
 * @param {Node} main Main element of the page
 */
export default class Modal {
  /**
   * Modal constructor
   * @param  {String}   id                       An ID to apply to the modal.
   * @param  {Object}   content                  Content object to inject in the
   *                                             modal.
   * @param  {String}   content.header           Content for the header.
   * @param  {String}   content.body             Content for the body.
   * @param  {String}   content.footer           Content for the footer.
   * @param  {Object}   [options={}]             Options object for customise
   *                                             behaviour of modal.
   * @param  {Node}     [options.main]           An element to treat as the main
   *                                             content so that
   *                                             aria-hidden="true" can be
   *                                             toggled. Defaults to #main.
   * @param  {Boolean}  [options.closeButton]    Shows the x close button.
   *                                             Defaults to true.
   * @param  {Boolean}  [options.backdropClose]  Enables closing of modal via
   *                                             clicking on the backdrop.
   *                                             Defaults to true.
   * @param  {String}   [options.extraClass]     Adds extra classes to the
   *                                             modal.
   * @param  {String}   [options.size]           Sets the size of the modal.
   * @param  {String}   [options.type]           Sets the type of the modal.
   * @param  {Function} [options.onShow]         Callback function after showing
   *                                             the modal.
   * @param  {Function} [options.onClose]        Callback function after closing
   *                                             the modal.
   * @return {Modal}                             Modal instance.
   */
  constructor(id, content, opts) {
    // Merge user options with defaultOptions
    const options = Object.assign({}, defaultOptions, opts);

    // Build configuration
    this.config = {
      id,
      content: {
        header: content.header,
        body: content.body,
        footer: content.footer,
      },
      options,
    };

    // Errors
    if (typeof this.config.id === 'undefined') {
      throw new TypeError('Modal id cannot be undefined.');
    }

    if (typeof this.config.content === 'undefined') {
      throw new TypeError('Modal content cannot be undefined.');
    }

    if (typeof this.config.options.main === 'undefined') {
      throw new TypeError('#main element does not exist.');
    }

    this.namespace = 'data-modal';
    this.body = document.body;
    this.main = this.config.options.main;

    // Appends markup to the body just befeore the </body>
    this.body.insertAdjacentHTML('beforeend', scaffold({
      uiIcon,
      id: this.config.id,
      header: this.config.content.header,
      body: this.config.content.body,
      footer: this.config.content.footer,
      closeButton: this.config.options.closeButton,
      backdropClose: this.config.options.backdropClose,
      extraClass:
        `${this.config.options.extraClass} ` +
        `${this.config.options.size} ` +
        `${this.config.options.type}`,
    }));

    this.node = document.getElementById(this.config.id);
    this.shown = false;

    // Bind show modal event to elements with <this.namespace>-show="<modal-id>"
    // data attributes
    Array.from(
    // Get all the elements with <this.namespace>-show="<modal-id>"
    document.querySelectorAll(
      `[${this.namespace}-show="${this.node.id}"]`),
    ).forEach((opener) => {
      opener.addEventListener('click', () => this.show());
    });

    // Bind hide modal event to elements with <this.namespace>-hide data
    // attributesmodules/membership/index.js
    Array.from(
      // Get all elements with <this.namespace>-hide inside the modal
      this.node.querySelectorAll(
        `[${this.namespace}-hide]`,
      ))
      // Then combine it with all elements on the page that targets the modal
      // with <this.namespace>-hide="<modal-id>" for hiding
      .concat(Array.from(document.querySelectorAll(
        `[${this.namespace}-hide="${this.node.id}"]`,
      )))
      .forEach((closer) => {
        closer.addEventListener('click', () => this.hide(CLOSE_EVENT.ELEMENT));
      });

    // Hides modal when the user clicks out of bounds
    this.node.addEventListener('click', (event) => {
      if (event.target.hasAttribute(`${this.namespace}-outbound`)) {
        this.hide(CLOSE_EVENT.OUTOFBOUNDS);
      }
    });

    document.addEventListener('keydown', (event) => {
      // keydown on Esc key
      if (this.shown && event.which === 27) {
        event.preventDefault();
        this.hide(CLOSE_EVENT.ESC);
      }

      // keydown on Tab key
      if (this.shown && event.which === 9) {
        this._trapTabKey(this.node, event);
      }
    });

    this.body.addEventListener('focus', (event) => {
      if (this.shown && !this.node.contains(event.target)) {
        this._setFocusToFirstItem(this.node);
      }
    }, true);

    return this;
  }

  /**
   * Helper function to get all focusable children from a node
   * @param  {Node} node Node to look in
   * @return {Array}     Array of focusable nodes
   */
  _getFocusableChildren(node) {
    const focusableElements = [
      'a[href]',
      'area[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button:not([disabled])',
      'iframe',
      'object',
      'embed',
      '[contenteditable]',
      '[tabindex]:not([tabindex^="-"])',
    ];

    return Array.from(node.querySelectorAll(focusableElements.join(',')))
      .filter(child => !!(child.offsetWidth || child.offsetHeight ||
        child.getClientRects().length),
    );
  }

  /**
   * Helper function trapping the tab key inside a node
   * @param  {Node} node    Node to trap tab key in
   * @param  {Object} event Event to intercept
   */
  _trapTabKey(node, event) {
    const focusableChildren = this._getFocusableChildren(node);
    const focusedItemIndex = focusableChildren.indexOf(document.activeElement);

    if (event.shiftKey && focusedItemIndex === 0) {
      focusableChildren[focusableChildren.length - 1].focus();
      event.preventDefault();
    } else if (!event.shiftKey &&
        focusedItemIndex === focusableChildren.length - 1) {
      focusableChildren[0].focus();
      event.preventDefault();
    }
  }

  /**
   * Helper function to focus first focusable item in node
   * @param {Node} node Node to search in for first focusable
   */
  _setFocusToFirstItem(node) {
    const focusableChildren = this._getFocusableChildren(node);
    if (focusableChildren.length) focusableChildren[0].focus();
  }

  /**
   * Checks if scrollbar is visible.
   * Ported from the bootstrap v3.3.6.
   */
  _checkScrollbar() {
    const fullWindowWidth = window.innerWidth;

    this.bodyIsOverflowing = this.body.clientWidth < fullWindowWidth;
    this.scrollbarWidth = this._measureScrollbar();
  }

  /**
   * Sets the scrollbar padding offset on the body element.
   * Ported from the bootstrap v3.3.6.
   */
  _setScrollbar() {
    const bodyPadding = parseInt(this.body.style.paddingRight || 0, 10);

    this.originalBodyPadding = this.body.style.paddingRight || '';

    if (this.bodyIsOverflowing) {
      this.body.style.paddingRight = `${bodyPadding}${this.scrollbarWidth}px`;
    }
  }

  /**
   * Resets the padding on the body to the way it was with the scrollbar.
   * Ported from the bootstrap v3.3.6.
   */
  _resetScrollbar() {
    this.body.style.paddingRight = this.originalBodyPadding;
  }

  /**
   * Measures the width of the scrollbar.
   * Ported from the bootstrap v3.3.6.
   *
   * @return {Number} Width of the scrollbar.
   */
  _measureScrollbar() {
    const scrollDiv = document.createElement('div');
    // Injects some styling to the element which forces the display of a scroll
    // bar and places it off the page to ensure it is never visible.
    scrollDiv.style.position = 'absolute';
    scrollDiv.style.overflow = 'scroll';
    scrollDiv.style.top = '-9999px';
    scrollDiv.style.width = '100px';
    scrollDiv.style.height = '100px';

    this.body.appendChild(scrollDiv);
    const scrollbarWidth = scrollDiv.offsetWidth - scrollDiv.clientWidth;
    this.body.removeChild(scrollDiv);
    return scrollbarWidth;
  }

  /**
   * Show the modal with optional eventType identifier
   * @param  {String} eventType An identifier string to pass into the onShow
   *                            callback method.
   */
  show(eventType) {
    this.shown = true;
    this._checkScrollbar();
    this._setScrollbar();
    this.body.setAttribute(`${this.namespace}-open`, '');
    this.node.removeAttribute('aria-hidden');
    this.main.setAttribute('aria-hidden', 'true');
    this.focusedBeforeDialog = document.activeElement;
    this._setFocusToFirstItem(this.node);
    eventType = eventType || OPEN_EVENT.METHOD;

    if (this.config.options.onShow) {
      this.config.options.onShow(eventType);
    }
  }

  /**
   * Hide the modal
   * @param  {String} eventType An identifier string to pass into the onHide
   *                            callback method.
   */
  hide(eventType) {
    this.shown = false;
    this._resetScrollbar();
    this.body.removeAttribute(`${this.namespace}-open`, '');
    this.node.setAttribute('aria-hidden', 'true');
    this.main.removeAttribute('aria-hidden');
    this.focusedBeforeDialog.focus();

    if (this.config.options.onHide) {
      this.config.options.onHide(eventType);
    }
  }
}
